import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import type { IAtributo, IAtributoOpcao } from "interfaces/loja";
import {
  actionIconSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "../atributos.theme";
import {
  isHexColor,
  permiteOpcoes,
  TIPO_LABELS,
  tipoChipSx,
} from "./atributos.helpers";

type Props = {
  open: boolean;
  atributo: IAtributo | null;
  onClose: () => void;
  onAddOpcao: () => void;
  onEditOpcao: (opcao: IAtributoOpcao) => void;
  onDeleteOpcao: (opcao: IAtributoOpcao) => void;
};

export default function AtributoOpcoesDialog({
  open,
  atributo,
  onClose,
  onAddOpcao,
  onEditOpcao,
  onDeleteOpcao,
}: Props) {
  if (!atributo) return null;

  const temOpcoes = permiteOpcoes(atributo.tipo);
  const chip = tipoChipSx(atributo.tipo);
  const opcoes = atributo.opcoes ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { ...modalPaperSx, maxWidth: 640 } }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <TuneOutlinedIcon sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                Opções do atributo
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {atributo.nome}
              </Typography>
              <Box mt={0.75}>
                <Chip
                  size="small"
                  label={TIPO_LABELS[atributo.tipo]}
                  sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 600 }}
                />
              </Box>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <MDBox sx={{ p: 3 }}>
        {!temOpcoes ? (
          <MDTypography variant="body2" color="text.secondary">
            O tipo <strong>{TIPO_LABELS[atributo.tipo]}</strong> não utiliza opções
            pré-cadastradas. O valor é informado diretamente na variação do produto.
          </MDTypography>
        ) : opcoes.length === 0 ? (
          <MDBox
            sx={{
              py: 4,
              textAlign: "center",
              border: "1px dashed",
              borderColor: "grey.300",
              borderRadius: 2,
              bgcolor: "grey.50",
            }}
          >
            <MDTypography variant="body2" color="text.secondary" mb={2}>
              Nenhuma opção cadastrada ainda.
            </MDTypography>
            <MDButton
              variant="gradient"
              color="dark"
              startIcon={<AddOutlinedIcon />}
              onClick={onAddOpcao}
              sx={saveButtonSx}
            >
              Adicionar primeira opção
            </MDButton>
          </MDBox>
        ) : (
          <Stack spacing={1}>
            {opcoes.map((op) => (
              <MDBox
                key={op.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={1.5}
                py={1}
                sx={{
                  border: "1px solid",
                  borderColor: "grey.200",
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "rgba(249, 96, 48, 0.03)" },
                }}
              >
                <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                  {atributo.tipo === "COR" && isHexColor(op.valor) ? (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 0.75,
                        bgcolor: op.valor,
                        border: "1px solid",
                        borderColor: "grey.300",
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                  <Box minWidth={0}>
                    <MDTypography variant="button" fontWeight="medium" noWrap>
                      {op.label}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" display="block" noWrap>
                      {op.valor}
                    </MDTypography>
                  </Box>
                </MDBox>
                <MDBox display="flex" flexShrink={0}>
                  <IconButton size="small" onClick={() => onEditOpcao(op)} sx={actionIconSx}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onDeleteOpcao(op)}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </MDBox>
              </MDBox>
            ))}
          </Stack>
        )}
      </MDBox>

      {temOpcoes && opcoes.length > 0 ? (
        <MDBox
          px={3}
          py={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderTop="1px solid"
          borderColor="grey.200"
        >
          <MDTypography variant="caption" color="text.secondary">
            {opcoes.length} opção{opcoes.length === 1 ? "" : "ões"}
          </MDTypography>
          <Stack direction="row" spacing={1}>
            <MDButton variant="outlined" color="dark" onClick={onClose}>
              Fechar
            </MDButton>
            <MDButton
              variant="gradient"
              color="dark"
              startIcon={<AddOutlinedIcon />}
              onClick={onAddOpcao}
              sx={saveButtonSx}
            >
              Nova opção
            </MDButton>
          </Stack>
        </MDBox>
      ) : (
        <MDBox px={3} py={2} display="flex" justifyContent="flex-end" borderTop="1px solid" borderColor="grey.200">
          <MDButton variant="outlined" color="dark" onClick={onClose}>
            Fechar
          </MDButton>
        </MDBox>
      )}
    </Dialog>
  );
}
